import "@capone/checkout/build/assets/style.css";
import { isCorpTenant, UserSourceProvider } from "@capone/common";
import { ExperimentsHookProvider } from "@capone/experiments";
import {
  Box,
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core";
import { useMedalliaFeedback } from "b2b-base/src/components/MedalliaFeedback";
import { AgentBanner, TravelerWorkflowDialog, useDeviceTypes } from "halifax";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { Provider, useDispatch, useSelector } from "react-redux";
import { Route, Router, Switch, useLocation } from "react-router-dom";
import queryStringParser from "query-string";
import {
  CallState,
  CorePackagesCheckout,
  CorePackagesCheckoutV2,
  CorpSessionInfo,
  CustomEvents,
  EditTravelerModalState,
  IPerson,
  ModalNames,
  PackagesClientAssetProps,
  PackagesModuleProps,
  SavedTravelersProperties,
  SelfServeEvents,
  SessionInfo,
} from "redmond";
import { useSeenModalUpdater } from "../../../cap1-application/b2b-base/src/hooks/useSeenModalUpdater";
import { config } from "./api/config";
import { trackEvent } from "./api/v0/analytics/trackEvent";
import fetchUserInfo from "./api/v0/user/fetchUserInfo";
import AxiosInterceptors from "./components/AxiosInterceptors";
import ExperimentsProvider from "./context/experiments";
import useWindowEventListener from "./hooks/useWindowEventListener";
import { useProjectsAndCategoriesPrefetch } from "./hooks/useProjectsAndCategoriesPrefetch";
import * as englishTranslations from "./lang/en.json";
import * as frenchTranslations from "./lang/fr.json";
import { PackagesHotelsAvailability } from "./modules/availability";
import { PackagesBook } from "./modules/book";
import {
  deleteUserPassenger,
  fetchCorpUserPassengers,
  updateUserPassenger,
} from "./modules/book/actions/actions";
import { getUserPassengers } from "./modules/book/reducer";
import { PackagesFlightShop } from "./modules/flight-shop";
import { PackageHotelShop } from "./modules/hotel-shop";
import { PackageItineraryReview } from "./modules/itinerary-review";
import { actions as packageIdentityActions } from "./modules/package-identity/actions";
import { getPackageSearchKey } from "./modules/package-identity/reducer/selectors";
import { RewardsBanner } from "./modules/rewards/components";
import { getRewardsAccounts } from "./modules/rewards/reducer/selectors";
import { PackagesSearch } from "./modules/search";
import { store } from "./store";
import { eventsToListen } from "./utils/events";
import {
  PATH_BOOK,
  PATH_BOOK_CONFIRMATION,
  PATH_FLIGHT_SHOP,
  PATH_HOME,
  PATH_HOME_SEARCH,
  PATH_HOTEL_SHOP,
  PATH_HOTELS_AVAILABILITY,
  PATH_REVIEW,
} from "./utils/paths";

function loadLocaleData(locale: string): any {
  switch (locale) {
    case "fr":
      return frenchTranslations;
    default:
      return englishTranslations;
  }
}
const generateClassName = createGenerateClassName({
  productionPrefix: "ptPackagesModule",
  seed: "ptPackagesModule",
});

interface PackagesClientContextType extends PackagesClientAssetProps {
  isAgentPortal: boolean;
  isAutoApprovalEnabled: boolean;
  showCoreHomepage?: boolean;
}

export const ClientContext = React.createContext<
  Partial<PackagesClientContextType>
>({});

const App = (props: PackagesModuleProps) => {
  const {
    experiments,
    baseHistory,
    isAgentPortal,
    clientAssets,
    showCoreHomepage,
    core,
    coreV2,
  } = props;

  const [activeTheme, setActiveTheme] = useState(props.theme);
  const [locale, setLocale] = useState(props.language);
  const [messages, setMessages] = useState(loadLocaleData(locale).default);
  const [sessionInfo, setSessionInfo] = useState(clientAssets.sessionInfo);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userInfoResponse = await fetchUserInfo();
        setSessionInfo(userInfoResponse);
      } catch (error) {
        console.error(error);
      }
    };
    fetchUser();
  }, []);

  const handleThemeChanged = (e: CustomEvent) => {
    setActiveTheme(e.detail);
    console.log("THEME RECEIVED:", e.detail.palette.type);
  };
  const handleLocaleChanged = (e: CustomEvent) => {
    setLocale(e.detail);
    const messages = loadLocaleData(e.detail);
    setMessages(messages.default);
  };
  useWindowEventListener(eventsToListen.HOST_THEME_CHANGED, handleThemeChanged);
  useWindowEventListener(
    eventsToListen.HOST_LOCALE_CHANGED,
    handleLocaleChanged
  );

  const { firstName, lastName } = sessionInfo?.userInfo || {
    firstName: "",
    lastName: "",
  };

  return (
    <Provider store={store}>
      <ExperimentsProvider
        initState={{
          experiments: experiments?.experiments || [],
          trackingProperties: experiments?.trackingProperties,
          callState: CallState.NotCalled,
        }}
      >
        <ExperimentsHookProvider isLoggedIn={Boolean(sessionInfo?.csrfToken)}>
          <UserSourceProvider>
            <Router history={baseHistory}>
              <AxiosInterceptors isAgentPortal={isAgentPortal} />
              <ClientContext.Provider
                value={{ ...clientAssets, sessionInfo, showCoreHomepage }}
              >
                <div className="App">
                  <StylesProvider generateClassName={generateClassName}>
                    <ThemeProvider theme={activeTheme}>
                      {messages != null ? (
                        <IntlProvider
                          locale={locale}
                          defaultLocale="en"
                          messages={messages}
                        >
                          <Switch>
                            {isAgentPortal ? (
                              <Route path="*">
                                <AgentBanner
                                  firstName={firstName}
                                  lastName={lastName}
                                />
                              </Route>
                            ) : (
                              <Route path="*">
                                <Box />
                                <RewardsBanner />
                              </Route>
                            )}
                          </Switch>
                          <Body core={core} coreV2={coreV2} />
                        </IntlProvider>
                      ) : (
                        <div>Loading</div>
                      )}
                    </ThemeProvider>
                  </StylesProvider>
                </div>
              </ClientContext.Provider>
            </Router>
          </UserSourceProvider>
        </ExperimentsHookProvider>
      </ExperimentsProvider>
    </Provider>
  );
};

export const Body = ({
  core,
  coreV2,
}: {
  core?: CorePackagesCheckout;
  coreV2?: CorePackagesCheckoutV2;
}) => {
  useMedalliaFeedback();
  const dispatch = useDispatch();
  const location = useLocation();
  const { matchesMobile } = useDeviceTypes();
  const travelers = useSelector(getUserPassengers);
  const { sessionInfo: clientSessionInfo } = useContext(ClientContext);
  const [editTravelersOpen, setEditTravelersOpen] =
    useState<EditTravelerModalState>(EditTravelerModalState.closed);
  const [sessionInfo, setSessionInfo] = useState<
    SessionInfo | CorpSessionInfo
  >();

  const closeEditTravelersModal = useCallback(() => {
    setEditTravelersOpen(EditTravelerModalState.closed);
  }, []);

  const onEditTravelersStateChange = useCallback((ev: any) => {
    const newEditModalState =
      (ev as CustomEvent).detail ?? EditTravelerModalState.closed;

    setEditTravelersOpen(newEditModalState);
  }, []);

  const handleAddPass = useCallback(() => {
    if (editTravelersOpen === EditTravelerModalState.flight) {
      trackEvent({
        eventName: SelfServeEvents.ViewedSavedTravelers,
        properties: {
          url: window.location.pathname,
        },
      });
    }
  }, [editTravelersOpen]);

  const rewardsAccounts = useSelector(getRewardsAccounts, (a, b) => {
    if (a.length !== b.length) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    if (isCorpTenant(config.TENANT) && rewardsAccounts.length > 0) {
      dispatch(fetchCorpUserPassengers());
    }
  }, [rewardsAccounts]);

  // Prefetched once per active account and held in RTK Query's cache for the
  // session; re-fires automatically whenever the active account switches.
  useProjectsAndCategoriesPrefetch(clientSessionInfo);
  // Hydrate searchKey from the URL (set by b2bportal) once, then Redux is authoritative —
  // mirrors b2bportal's useResumePackageSearch, so internal navigation can't clobber it.
  const currentSearchKey = useSelector(getPackageSearchKey);
  useEffect(() => {
    if (currentSearchKey != null) return;

    const { packageSearchKey } = queryStringParser.parse(location.search);

    if (typeof packageSearchKey !== "string") return;

    dispatch(packageIdentityActions.setSearchKey(packageSearchKey));
  }, [location.search, currentSearchKey, dispatch]);

  const hasSeenNfuInfoModal =
    sessionInfo && "corporateInfo" in sessionInfo
      ? sessionInfo.corporateInfo?.hasSeenModalMap?.[
          ModalNames.NFU_IN_PRODUCT_AWARENESS_PROFILES
        ]
      : false;

  const defaultTravelerId =
    sessionInfo && "corporateInfo" in sessionInfo
      ? sessionInfo.corporateInfo.defaultTravelerId
      : "";

  const userRoles =
    sessionInfo && "corporateInfo" in sessionInfo
      ? sessionInfo.corporateInfo.role
      : [];

  useEffect(() => {
    document.addEventListener(
      CustomEvents.editTravelersStateChange,
      onEditTravelersStateChange
    );

    return () => {
      document.removeEventListener(
        CustomEvents.editTravelersStateChange,
        onEditTravelersStateChange
      );
    };
  }, []);

  return (
    <>
      <Box className="main-section">
        <Route path={[PATH_HOME, PATH_HOME_SEARCH]} exact>
          <PackagesSearch />
        </Route>
        <Route path={PATH_HOTELS_AVAILABILITY}>
          <PackagesHotelsAvailability />
        </Route>
        <Route path={[PATH_BOOK, PATH_BOOK_CONFIRMATION]}>
          <PackagesBook core={core} coreV2={coreV2} />
        </Route>
        <Route path={PATH_HOTEL_SHOP}>
          <PackageHotelShop />
        </Route>
        <Route path={PATH_FLIGHT_SHOP}>
          <PackagesFlightShop />
        </Route>
        <Route path={PATH_REVIEW}>
          <PackageItineraryReview core={core} coreV2={coreV2} />
        </Route>
      </Box>
      <TravelerWorkflowDialog
        tenant={config.TENANT}
        showHotelLoyaltySection
        showGroundLoyaltySection
        disableDriversLicenseValidation
        disallowSelect
        displayModalSubtitle
        showAdditionalInfoSection
        showDriverLicenseSection
        showFrequentFlyerSection
        showGenderField
        showNationalityField
        buttonClassName="b2b"
        onAddPass={handleAddPass}
        dialogProps={{
          className: "packages-edit-traveler",
          onBackdropClick: closeEditTravelersModal,
          onClose: closeEditTravelersModal,
          open: editTravelersOpen === EditTravelerModalState.packages,
        }}
        errorMessage="Add or choose one traveler to continue."
        fullScreenWithBanner={matchesMobile}
        handleDeletePassenger={(personId: string) => {
          dispatch(deleteUserPassenger({ personId }));
        }}
        handleUpdatePassenger={(
          person: IPerson,
          hideProfileAction,
          entryPoint?: string,
          updatePassport?: boolean
        ) => {
          dispatch(
            updateUserPassenger(
              { person, hideProfileAction },
              false,
              updatePassport,
              false,
              false,
              defaultTravelerId,
              entryPoint
            )
          );

          const frequentFlyerProgramsTotal = Reflect.ownKeys(
            person.frequentFlyer
          ).length;

          if (!isCorpTenant(config.TENANT)) {
            trackEvent({
              eventName: SelfServeEvents.ClickSavedTravelersSaveTraveler,
              properties: {
                url: window.location.pathname,
                frequent_flyer_program_added: frequentFlyerProgramsTotal > 0,
                num_frequent_flyer_programs: frequentFlyerProgramsTotal,
              } satisfies SavedTravelersProperties,
            });
          }
        }}
        isMobile={matchesMobile}
        travelers={travelers}
        titles={{}}
        dialogContentProps={{}}
        userRoles={userRoles}
        trackEvent={trackEvent}
        defaultTravelerId={defaultTravelerId}
        hasSeenNfuInfo={hasSeenNfuInfoModal}
        handleSeenNfuInfoModal={useSeenModalUpdater(
          sessionInfo,
          setSessionInfo
        )}
      />
    </>
  );
};
export default App;
